/** @format */

import axios, { type AxiosHeaders, type InternalAxiosRequestConfig } from 'axios';
import { keycloakClient } from 'src/contexts/KeyCloakContext';
import { getAppLanguage, getContentLanguage } from 'src/utils/locale';
import { resolveExtranetApiLanguageCode } from 'src/utils/extranetLanguages';

declare module 'axios' {
  export interface AxiosRequestConfig {
    acceptLanguage?: string;
  }
}

const resolveAcceptLanguage = (locale?: string): string => {
  const raw = locale ? getContentLanguage(locale) : getAppLanguage();
  return resolveExtranetApiLanguageCode(raw) || raw || 'en';
};

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL
});

const setRequestHeader = (
  headers: InternalAxiosRequestConfig['headers'] | undefined,
  name: string,
  value: string
): InternalAxiosRequestConfig['headers'] => {
  if (!headers) {
    return { [name]: value } as InternalAxiosRequestConfig['headers'];
  }

  if (typeof (headers as AxiosHeaders).set === 'function') {
    (headers as AxiosHeaders).set(name, value);
    return headers;
  }

  (headers as Record<string, string>)[name] = value;
  return headers;
};

axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    if (keycloakClient.token) {
      const isTokenExpired = await keycloakClient.isTokenExpired();
      if (isTokenExpired) {
        await keycloakClient.updateToken().catch((error: unknown) => {
          console.error(error);
        });
        axiosInstance.defaults.headers.common.Authorization = `Bearer ${keycloakClient.token}`;
        config.headers = setRequestHeader(config.headers, 'Authorization', `Bearer ${keycloakClient.token}`);
      }
    }

    const acceptLanguage = resolveAcceptLanguage(config.acceptLanguage);
    config.headers = setRequestHeader(config.headers, 'Accept-Language', acceptLanguage);

    return config;
  },
  () => {
    return Promise.reject('Something went wrong');
  }
);

axiosInstance.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    if (!error?.response) {
      return Promise.reject(error?.message || 'Something went wrong');
    }

    const { data, status } = error.response;

    // Keep HTTP status so callers can map errors like 413 Content Too Large
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      return Promise.reject({ ...data, status });
    }

    if (typeof data === 'string' && data.trim()) {
      return Promise.reject({ message: data, status });
    }

    return Promise.reject({ message: 'Something went wrong', status });
  }
);

export default axiosInstance;
